import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AdministradoraService } from '../services/administradora/administradora.service';
import { AuthService } from '../services/auth/auth.service';
import { LayoutService } from '../services/layout/layout.service';
import { LoaderService } from '../services/loader/loader.service';
import { NotificationService } from '../services/notification/notification.service';
import * as CoreActions from './actions';
import { IAppState } from './state';
import {
  selectICodSegurado,
  selectIdentificacaoRequest,
  selectTenantSlug,
  selectTokenSessaoLogin,
} from './selectors';
import { apiErrorMessage } from './api-error.util';

@Injectable()
export class CoreEffects {
  /**
   * Boot do tenant: disparado pelo TenantResolver (core/resolvers/tenant)
   * assim que o primeiro segmento da URL (`:tenant`) é resolvido. Carrega os
   * parâmetros da Administradora e, em caso de sucesso, aplica o tema (ver
   * `applyAdministradoraTheme$` abaixo) — réplica do padrão `initEnterprise$`
   * descrito no ARQUITETURA.md do portal-cliente-frontend, adaptado para o
   * modelo de parâmetros chave-valor confirmado neste app v2.
   */
  loadAdministradoraAttempt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadAdministradoraAttempt),
      tap(({ tenantSlug }) => this.store.dispatch(CoreActions.setTenantSlug({ tenantSlug }))),
      switchMap(({ tenantSlug }) =>
        this.administradoraService.getAdministradora(tenantSlug).pipe(
          map((administradora) =>
            // HTTP 204 (sem cadastro) chega aqui como `null` — caso real
            // confirmado para o tenant affixbeneficios em homologação
            // (24/08/2026).
            CoreActions.loadAdministradoraSuccess({ administradora })
          ),
          catchError((error) =>
            of(
              CoreActions.loadAdministradoraError({
                error: apiErrorMessage(error, 'Não foi possível carregar a configuração do tenant.'),
              })
            )
          )
        )
      )
    )
  );

  applyAdministradoraTheme$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActions.loadAdministradoraSuccess),
        tap(({ administradora }) => {
          this.administradoraService.setAdministradora(administradora);
          this.layoutService.applyTheme(administradora?.parametros ?? null);
        })
      ),
    { dispatch: false }
  );

  /**
   * PASSO 1 do login (identificação por CPF/CNPJ + data de nascimento).
   * CONFIRMADO em 26/08/2026 (homologação) que são DUAS chamadas em
   * sequência, não uma só:
   * 1. `PossuiSenhaExterna` — identifica o segurado (`iCodSegurado`).
   * 2. `Token/AtualizarTokenSessao` — devolve o token de SESSÃO de curta
   *    duração que o passo 2 (`LoginExterno`) exige no lugar do
   *    `partnerToken` estático (CONFIRMADO: mandar o `partnerToken` no
   *    passo 2 resulta em 401 "Usuário sem permissão de acesso").
   *
   * `LoginExternoResumido` (visto na mesma janela de rede do app real)
   * ficou de FORA de propósito: testamos chamá-la aqui com o mesmo corpo de
   * `PossuiSenhaExterna` e o backend respondeu 400 "A senha é obrigatória"
   * — ou seja, ao contrário do que tínhamos assumido, ela NÃO roda (ou não
   * roda só) no passo 1 sem senha. Sem confirmar o contrato/gatilho real,
   * não faz sentido replicá-la aqui; o passo 1 funciona sem ela.
   * TODO: confirmar o papel real de `LoginExternoResumido` (talvez faça
   * parte do passo 2, junto com `LoginExterno`).
   *
   * Em caso de sucesso, o reducer troca `loginEtapa` para `'senha'` —
   * CONFIRMADO que isso acontece na MESMA URL `/{tenant}/`, sem navegação
   * de rota (ver state.ts).
   */
  identificarAttempt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.identificarAttempt),
      tap(() => this.loader.show()),
      switchMap(({ request }) =>
        this.authService.possuiSenhaExterna(request).pipe(
          switchMap((identificarResponse) =>
            this.authService.atualizarTokenSessao({ chave: request.sCPF, tipo: 0 }).pipe(
              map(({ token }) =>
                CoreActions.identificarSuccess({
                  response: identificarResponse,
                  tokenSessaoLogin: token,
                })
              )
            )
          ),
          catchError((error) =>
            of(
              CoreActions.identificarError({
                error: apiErrorMessage(error, 'Não foi possível continuar. Verifique os dados informados.'),
              })
            )
          ),
          tap(() => this.loader.hide())
        )
      )
    )
  );

  identificarError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActions.identificarError),
        tap(({ error }) => this.notification.error(error, 'Erro'))
      ),
    { dispatch: false }
  );

  /**
   * PASSO 2 do login (senha, endpoint confirmado `LoginExterno`). Contrato
   * TOTALMENTE diferente do que tínhamos assumido — CONFIRMADO em
   * 25/08/2026 (produção) inspecionando só as CHAVES do request real (nunca
   * a senha): `{ sLoginExterno, sSenhaLogin, iCodSegurado, iCodAdm,
   * uraLog }`, não `{ ...IdentificarRequest, sSenha }`. `iCodSegurado` vem
   * da RESPOSTA do passo 1 (ver `identificarSuccess`/`selectICodSegurado`).
   * Ver core/models/auth.model.ts para o detalhe de cada campo.
   */
  loginAttempt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loginAttempt),
      withLatestFrom(
        this.store.select(selectIdentificacaoRequest),
        this.store.select(selectICodSegurado),
        this.store.select(selectTokenSessaoLogin)
      ),
      tap(() => this.loader.show()),
      switchMap(([{ senha }, identificacaoRequest, iCodSegurado, tokenSessaoLogin]) =>
        this.authService
          .loginExterno(
            {
              sLoginExterno: identificacaoRequest?.sCPF ?? '',
              sSenhaLogin: senha,
              iCodSegurado: iCodSegurado ?? 0,
              iCodAdm: identificacaoRequest?.iCodAdm ?? 0,
              // CONFIRMADO em 25/08/2026 (produção) — não é booleano, ver
              // comentário completo em core/models/auth.model.ts.
              uraLog: { iSeqUraLog: 0, iSeqInterface: 2 },
            },
            // CONFIRMADO em 26/08/2026 (homologação): token de sessão do
            // passo 1, não o partnerToken — ver AuthService.loginExterno.
            tokenSessaoLogin ?? ''
          )
          .pipe(
            // CONFIRMADO em 25/08/2026 (produção): senha errada chega como
            // HTTP 200 com `{ sucesso: false, mensagem: "Senha Errada." }`,
            // não como erro HTTP — precisa checar `sucesso` explicitamente
            // (ver LoginResponse em core/models/auth.model.ts).
            map((response) =>
              response.sucesso
                ? CoreActions.loginSuccess({ response })
                : CoreActions.loginError({ error: response.mensagem || 'Não foi possível entrar.' })
            ),
            catchError((error) =>
              of(
                CoreActions.loginError({
                  error: apiErrorMessage(error, 'Senha incorreta. Tente novamente.'),
                })
              )
            ),
            tap(() => this.loader.hide())
          )
      )
    )
  );

  /**
   * O `LoginResponse` de sucesso devolve `solicitacao[]` (dados da
   * apólice/segurado), não um token de sessão novo — CONFIRMADO em
   * 26/08/2026 (homologação). Por isso reaproveitamos o `tokenSessaoLogin`
   * (de `Token/AtualizarTokenSessao`, ver `identificarAttempt$` acima) como
   * o token final do app — é o único token de sessão real que observamos em
   * todo o fluxo. `User` continua montado a partir do que já temos (dados
   * do passo 1); `solicitacao[]` tem dados pessoais reais e não foi
   * inspecionado/mapeado nesta versão (ver core/models/auth.model.ts).
   * TODO: confirmar se `tokenSessaoLogin` é mesmo o token certo para as
   * chamadas pós-login, ou se existe um token de sessão de vida mais longa
   * que ainda não identificamos.
   */
  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loginSuccess),
      withLatestFrom(
        this.store.select(selectIdentificacaoRequest),
        this.store.select(selectTokenSessaoLogin)
      ),
      map(([{ response }, identificacaoRequest, tokenSessaoLogin]) => {
        const documento = identificacaoRequest?.sCPF ?? '';
        // TODO: `solicitacao[0]` nunca foi inspecionado em detalhe (dados
        // pessoais reais, ver LoginResponse em auth.model.ts) — nomes de
        // campo abaixo são um PALPITE, não confirmados. `sDscOperadora` é
        // uma aposta razoável (mesmo nome já confirmado em
        // `IdentificarResponse.outrosContatos[].sDscOperadora`); `entidade`/
        // `numeroCarteirinha` não têm precedente confirmado. Verificar num
        // login real (console apenas, nunca persistir) antes de confiar
        // nestes valores em produção.
        const primeiraSolicitacao = response.solicitacao?.[0];
        return CoreActions.setAuthData({
          user: {
            documento,
            // Derivado do tamanho do documento (11 = CPF/PF) — ver comentário
            // completo em core/models/user.model.ts.
            tipoPessoa: documento.length === 11 ? 'PF' : 'PJ',
            operadora: primeiraSolicitacao?.['sDscOperadora'] as string | undefined,
            entidade: primeiraSolicitacao?.['sEmpresa'] as string | undefined,
            numeroCarteirinha: primeiraSolicitacao?.['sNumeroCarteirinha'] as string | undefined,
          },
          token: tokenSessaoLogin ?? '',
        });
      })
    )
  );

  setAuthData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActions.setAuthData),
        withLatestFrom(this.store.select(selectTenantSlug)),
        tap(([, tenantSlug]) => this.router.navigate([`/${tenantSlug}/cliente`]))
      ),
    { dispatch: false }
  );

  loginError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActions.loginError),
        tap(({ error }) => this.notification.error(error, 'Erro'))
      ),
    { dispatch: false }
  );

  signOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreActions.signOut),
        withLatestFrom(this.store.select(selectTenantSlug)),
        tap(([, tenantSlug]) => this.router.navigate([`/${tenantSlug ?? ''}`]))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private administradoraService: AdministradoraService,
    private authService: AuthService,
    private layoutService: LayoutService,
    private loader: LoaderService,
    private notification: NotificationService,
    private router: Router,
    private store: Store<IAppState>
  ) {}
}
